import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "styled-components";
import { FiAlertTriangle, FiDownload, FiFileText, FiInfo, FiList, FiRefreshCw, FiSearch, FiX } from "react-icons/fi";
import {
  Empty, Group, IconButton, IconTile, Meter, SearchField, Section, SectionHeader, Skeleton, tokens,
} from "../../activity-logs/styles";
import { SegmentedControl } from "../../activity-logs/components/primitives";
import { ActionButton, DirectoryCard, Notice, SupplierGrid, Tag, TaxBar } from "../styles";
import { TAXES, categoryMeta, formatCount, formatCurrency, formatPercent, taxMeta } from "../dashboardData";
import { buildItemStats, formatDate } from "../detailData";
import { documentTitle, downloadXml, nameForItem, useXmlInfo } from "../xmlDocs";
import { MoneyValue } from "./parts";
import ItemSheet from "./ItemSheet";

/**
 * Documentos e itens do fornecedor em cards, no fim da Visão geral.
 * Os itens chegam aos poucos (useSupplierItems); os cards aparecem conforme a
 * rolagem, 36 por vez, e a lista se reordena sozinha enquanto a carga avança.
 * Por item agrupa os créditos por NCM (sem NCM, pelo tipo de crédito). Nome do
 * produto e XML só existem quando a API manda a chave do documento.
 */
const STEP = 36;
const shortKey = (nfe) => (nfe && nfe.length > 14 ? `…${nfe.slice(-10)}` : nfe || "Sem documento");
const creditOf = (i) => i.creditoRecuperavel || 0;
export const formatNcm = (ncm) => (/^\d{8}$/.test(String(ncm)) ? String(ncm).replace(/^(\d{4})(\d{2})(\d{2})$/, "$1.$2.$3") : String(ncm || ""));

// Soma por imposto dos itens, na ordem fixa de TAXES
const taxSplit = (items) => TAXES
  .map((t) => ({ ...t, total: items.filter((i) => i.credito?.tipo === t.key).reduce((s, i) => s + creditOf(i), 0) }))
  .filter((t) => t.total > 0);

// Um grupo por NCM; itens sem NCM (frete) ficam juntos pelo tipo de crédito
const groupByNcm = (items) => {
  const map = new Map();
  items.forEach((i) => {
    const key = i.ncm ? `ncm:${i.ncm}` : `tipo:${i.tipoCredito}`;
    let g = map.get(key);
    if (!g) {
      g = { key, ncm: i.ncm ? String(i.ncm) : "", credito: i.credito, items: [], total: 0, base: 0, last: "", docs: new Set(), cfops: new Set() };
      map.set(key, g);
    }
    g.items.push(i);
    g.total += creditOf(i);
    g.base += i.valorProduto || 0;
    if ((i.dataEmissao || "") > g.last) g.last = i.dataEmissao;
    if (i.nfe) g.docs.add(i.nfe);
    if (i.cfop) g.cfops.add(String(i.cfop));
  });
  return [...map.values()]
    .map((g) => ({
      ...g,
      label: g.ncm ? `NCM ${formatNcm(g.ncm)}` : g.credito?.titulo || "Sem NCM",
      docs: g.docs.size,
      cfops: [...g.cfops],
      items: g.items.sort((a, b) => creditOf(b) - creditOf(a)),
    }))
    .sort((a, b) => b.total - a.total);
};

// Botão de XML sobreposto ao card: fica fora do <button> do card para não aninhar botões
const XmlButton = ({ chave }) => (
  <IconButton
    type="button"
    title="Baixar XML"
    aria-label="Baixar XML"
    onClick={() => downloadXml(chave)}
    style={{ position: "absolute", top: 12, right: 12, width: 30, height: 30, fontSize: 13 }}
  >
    <FiDownload />
  </IconButton>
);

function DocumentCard({ doc, delay, onOpen }) {
  const { info } = useXmlInfo(doc.nfe);
  const name = documentTitle(info);
  const split = taxSplit(doc.items);
  const main = split.reduce((best, t) => (!best || t.total > best.total ? t : best), null) || TAXES[0];
  return (
    <div style={{ position: "relative", display: "flex" }}>
      <DirectoryCard type="button" $delay={delay} onClick={() => onOpen(doc)} style={{ flex: 1, minWidth: 0 }}>
        <span className="head">
          <IconTile $size={40} $color={main.color}><FiFileText /></IconTile>
          <span className="who" style={{ paddingRight: 32 }}>
            <span className="name" title={name || doc.nfe}>{name || shortKey(doc.nfe)}</span>
            <span className="id">
              {[name && shortKey(doc.nfe), formatDate(doc.date), `${formatCount(doc.items.length)} ${doc.items.length === 1 ? "item" : "itens"}`].filter(Boolean).join(" · ")}
            </span>
          </span>
        </span>
        <span className="body">
          <span className="amount"><MoneyValue value={doc.total} /></span>
          <span className="meta">base {formatCurrency(doc.base)}</span>
          <TaxBar className="bar">
            {split.map((t) => <span key={t.key} title={`${t.label}: ${formatCurrency(t.total)}`} style={{ flexGrow: t.total, background: t.color }} />)}
          </TaxBar>
          <span className="taxes">{split.map((t) => <Tag key={t.key} $color={t.color}>{t.label}</Tag>)}</span>
        </span>
      </DirectoryCard>
      <XmlButton chave={doc.nfe} />
    </div>
  );
}

// Nome do produto pelo XML da maior ocorrência com chave (só quando há NCM: no frete o grupo mistura cargas)
function useGroupName(group) {
  const sample = group.ncm ? group.items.find((i) => i.nfe) : null;
  const { info } = useXmlInfo(sample?.nfe);
  return sample && info ? nameForItem(info, sample) : "";
}

function GroupCard({ group, delay, onOpen }) {
  const name = useGroupName(group);
  const split = taxSplit(group.items);
  const main = split.reduce((best, t) => (!best || t.total > best.total ? t : best), null) || TAXES[0];
  const Icon = categoryMeta(group.credito?.categoria).icon;
  const n = group.items.length;
  return (
    <DirectoryCard type="button" $delay={delay} onClick={() => onOpen({ ...group, name })}>
      <span className="head">
        <IconTile $size={40} $color={main.color}><Icon /></IconTile>
        <span className="who">
          <span className="name" title={name || group.label}>{name || group.label}</span>
          <span className="id">
            {[name && group.label, `${formatCount(n)} ${n === 1 ? "ocorrência" : "ocorrências"}`, group.docs > 0 && `${formatCount(group.docs)} doc.`].filter(Boolean).join(" · ")}
          </span>
        </span>
      </span>
      <span className="body">
        <span className="amount"><MoneyValue value={group.total} /></span>
        <span className="meta">base {formatCurrency(group.base)}{n > 1 ? ` · média ${formatCurrency(group.total / n)}` : ""}</span>
        <TaxBar className="bar">
          {split.map((t) => <span key={t.key} title={`${t.label}: ${formatCurrency(t.total)}`} style={{ flexGrow: t.total, background: t.color }} />)}
        </TaxBar>
        <span className="taxes">
          {split.map((t) => <Tag key={t.key} $color={t.color}>{t.label}</Tag>)}
          {group.cfops.slice(0, 2).map((c) => <Tag key={c}>CFOP {c}</Tag>)}
          {group.cfops.length > 2 && <Tag>+{group.cfops.length - 2}</Tag>}
        </span>
      </span>
    </DirectoryCard>
  );
}

export default function SupplierRecords({ supplier, loader, onReach }) {
  const theme = useTheme();
  const [mode, setMode] = useState("documents");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("value");
  const [visible, setVisible] = useState(STEP);
  const [sheet, setSheet] = useState(null);
  const rootRef = useRef(null);
  const endRef = useRef(null);
  const closeSheet = useCallback(() => setSheet(null), []);

  // A carga só começa quando a seção se aproxima da tela
  useEffect(() => {
    const el = rootRef.current;
    if (!el || loader.started) return undefined;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) onReach(); }, { rootMargin: "600px" });
    io.observe(el);
    return () => io.disconnect();
  }, [loader.started, onReach]);

  // Só agrupa por documento o que tem chave: sem ela, cada item viraria um "documento"
  const keyed = useMemo(() => {
    const without = loader.items.filter((i) => !i.nfe);
    return {
      documents: buildItemStats(loader.items.filter((i) => i.nfe)).documents,
      missing: without.length,
      missingCredit: without.reduce((s, i) => s + creditOf(i), 0),
    };
  }, [loader.items]);

  const groups = useMemo(() => groupByNcm(loader.items), [loader.items]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (mode === "documents") {
      const list = keyed.documents.filter((d) => !term || d.nfe.toLowerCase().includes(term));
      if (sort === "recent") return [...list].sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));
      if (sort === "count") return [...list].sort((a, b) => b.items.length - a.items.length);
      return list;
    }
    const digits = term.replace(/D/g, "");
    const list = groups.filter((g) => !term
      || [g.label, g.credito?.titulo, ...g.cfops].some((v) => String(v || "").toLowerCase().includes(term))
      || (digits && g.ncm.startsWith(digits)));
    if (sort === "recent") return [...list].sort((a, b) => String(b.last || "").localeCompare(String(a.last || "")));
    if (sort === "count") return [...list].sort((a, b) => b.items.length - a.items.length);
    return list;
  }, [mode, sort, q, keyed.documents, groups]);

  // Rolagem infinita: mais 36 cards quando o fim da grade se aproxima
  const hasMore = visible < filtered.length;
  useEffect(() => {
    const el = endRef.current;
    if (!el || !hasMore) return undefined;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible((v) => v + STEP); }, { rootMargin: "800px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, filtered.length, visible]);

  const changeMode = (next) => { setMode(next); setVisible(STEP); };
  const progress = supplier.count > 0 ? Math.min(100, (loader.items.length / supplier.count) * 100) : 0;
  const loadedCredit = loader.items.reduce((s, i) => s + creditOf(i), 0);
  const capped = loader.done && loader.items.length < supplier.count;
  const sheetItem = sheet?.item || sheet?.document?.items[0] || sheet?.group?.items[0];
  const total = mode === "documents" ? keyed.documents.length : groups.length;

  let body;
  if (loader.error && loader.items.length === 0) {
    body = (
      <Group>
        <Empty>
          <div className="e-icon"><FiAlertTriangle /></div>
          <div className="e-title">Não foi possível carregar os itens</div>
          <div className="e-sub" style={{ marginBottom: 14 }}>Verifique a conexão e tente de novo.</div>
          <ActionButton onClick={loader.reload} style={{ margin: "0 auto" }}><FiRefreshCw /> Tentar novamente</ActionButton>
        </Empty>
      </Group>
    );
  } else if (loader.items.length === 0) {
    body = <SupplierGrid>{[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} $h={168} $r={18} />)}</SupplierGrid>;
  } else if (filtered.length === 0) {
    const noKeys = mode === "documents" && !q && keyed.documents.length === 0;
    body = (
      <Group>
        <Empty>
          <div className="e-icon">{noKeys ? <FiFileText /> : <FiSearch />}</div>
          <div className="e-title">{noKeys ? "Sem documentos identificados" : "Nenhum resultado"}</div>
          <div className="e-sub" style={{ marginBottom: noKeys ? 14 : 0 }}>
            {noKeys ? "A API não envia a chave do documento nos itens deste fornecedor." : `Nada encontrado para "${q}".`}
          </div>
          {noKeys && <ActionButton onClick={() => changeMode("items")} style={{ margin: "0 auto" }}><FiList /> Ver por item</ActionButton>}
        </Empty>
      </Group>
    );
  } else {
    body = (
      <SupplierGrid>
        {filtered.slice(0, visible).map((x, idx) => {
          const delay = Math.min(idx % STEP, 11) * 25;
          return mode === "documents"
            ? <DocumentCard key={x.key} doc={x} delay={delay} onOpen={(document) => setSheet({ document })} />
            : <GroupCard key={x.key} group={x} delay={delay} onOpen={(group) => setSheet({ group })} />;
        })}
      </SupplierGrid>
    );
  }

  return (
    <Section $delay={200} ref={rootRef}>
      <SectionHeader>
        <h2>Documentos e itens</h2>
        {loader.started && (
          <IconButton onClick={loader.reload} title="Recarregar itens" aria-label="Recarregar itens">
            <FiRefreshCw className={loader.loading ? "spin" : ""} />
          </IconButton>
        )}
      </SectionHeader>

      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <SegmentedControl
          small
          value={mode}
          onChange={changeMode}
          options={[
            { key: "documents", icon: <FiFileText />, label: "Por documento", count: loader.started ? keyed.documents.length : undefined },
            { key: "items", icon: <FiList />, label: "Por item", count: loader.started ? groups.length : undefined },
          ]}
        />
        <SearchField $max="360px">
          <FiSearch />
          <input
            placeholder={mode === "documents" ? "Buscar pela chave do documento" : "Buscar por NCM, CFOP ou tipo de crédito"}
            aria-label="Buscar"
            value={q}
            onChange={(e) => { setQ(e.target.value); setVisible(STEP); }}
          />
          {q && <button className="clear" aria-label="Limpar busca" onClick={() => setQ("")}><FiX /></button>}
        </SearchField>
        <div style={{ flex: 1 }} />
        <SegmentedControl
          small
          value={sort}
          onChange={(v) => { setSort(v); setVisible(STEP); }}
          options={[
            { key: "value", label: "Maior valor" },
            { key: "recent", label: "Recentes" },
            { key: "count", label: mode === "documents" ? "Mais itens" : "Mais ocorrências" },
          ]}
        />
      </div>

      {/* Progresso da carga: a ordem dos cards ainda pode mudar até o fim */}
      {loader.loading && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: "0.78em", color: tokens.label2(theme) }}>
            <span>Carregando itens da API · {formatCount(loader.items.length)} de {formatCount(supplier.count)}</span>
            <span>{formatPercent(progress)} · {formatPercent(supplier.total > 0 ? (loadedCredit / supplier.total) * 100 : 0)} do crédito</span>
          </div>
          <Meter $color={tokens.label2(theme)}><div style={{ width: `${progress}%` }} /></Meter>
        </div>
      )}

      {loader.error && loader.items.length > 0 && (
        <Notice style={{ marginBottom: 0 }}>
          <FiAlertTriangle />
          <span style={{ flex: 1 }}>A carga parou em {formatCount(loader.items.length)} itens por uma falha da API.</span>
          <ActionButton onClick={loader.resume} style={{ height: 28 }}>Continuar</ActionButton>
        </Notice>
      )}

      {capped && (
        <Notice style={{ marginBottom: 0 }}>
          <FiInfo />
          <span>
            A API entregou {formatCount(loader.items.length)} de {formatCount(supplier.count)} itens ({formatPercent(supplier.total > 0 ? (loadedCredit / supplier.total) * 100 : 0)} do crédito): a paginação para em 10.000 itens por tipo de crédito.
          </span>
        </Notice>
      )}

      {mode === "documents" && keyed.missing > 0 && keyed.documents.length > 0 && (
        <Notice style={{ marginBottom: 0 }}>
          <FiInfo />
          <span>
            {formatCount(keyed.missing)} {keyed.missing === 1 ? "item veio" : "itens vieram"} sem a chave do documento ({formatCurrency(keyed.missingCredit)}) e aparecem só em Por item.
          </span>
        </Notice>
      )}

      <div style={{ marginTop: 14 }}>{body}</div>

      {hasMore && (
        <div ref={endRef} style={{ padding: "18px 0", textAlign: "center", fontSize: "0.78em", color: tokens.label2(theme) }}>
          {formatCount(visible)} de {formatCount(total)}
        </div>
      )}

      {sheet && (
        <ItemSheet
          item={sheet.item}
          document={sheet.document}
          group={sheet.group}
          metodologias={loader.metodologias}
          metodologia={loader.metodologias[sheetItem?.tipoCredito]}
          color={taxMeta(sheetItem?.credito?.tipo).color}
          onClose={closeSheet}
        />
      )}
    </Section>
  );
}
